/**
 * Shop page state — the shop + its categories (sorted/grouped), its products, the in-shop search, the veg filter
 * for food shops, add-to-cart with the one-shop-per-cart prompt, and the category jump menu. Moved verbatim out of
 * RestaurantDetailPage so the classic view and the Blinkit view share one implementation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { isFoodShop } from "../../../lib/foodCategory";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenant } from "../../../contexts/TenantContext";
import { multiShopOn } from "../../../lib/multiShop";

export const UNCATEGORIZED = "__uncat__";

export function useShopController() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { tenant } = useTenant();
    const [rd, setRd] = useState(null);
    const [products, setProducts] = useState([]);
    const [verticals, setVerticals] = useState([]);
    const [q, setQ] = useState("");
    const [collapsed, setCollapsed] = useState({}); // {cat_id: bool}
    const [foodType, setFoodType] = useState(null); // VEG / NON_VEG / null
    const [menuOpen, setMenuOpen] = useState(false);
    const sectionRefs = useRef({});

    useEffect(() => {
        // Resolve default address coords so backend can compute distance
        // — see bug #11. Falls back to coord-less request for anonymous
        // visitors.
        (async () => {
            let q = "";
            try {
                const { data } = await api.get("/addresses");
                const def = (data || []).find((a) => a.is_default) || (data || [])[0];
                if (def?.lat != null && def?.lng != null) q = `?lat=${def.lat}&lng=${def.lng}`;
            } catch (err) { console.debug("[RestaurantDetail] /addresses fetch (anonymous user is OK):", err); }
            try {
                const { data } = await api.get(`/restaurants/${id}${q}`);
                setRd(data);
            } catch (err) { console.error("[RestaurantDetail] /restaurants/:id failed:", err); }
        })();
        api.get(`/restaurants/${id}/products`).then(({ data }) => setProducts(data));
        // Verticals catalog resolves UUID → slug so the catalog label + veg
        // filter both work whether the shop stores legacy slugs or new UUIDs.
        api.get(`/business-categories`).then(({ data }) => setVerticals(data || []))
            .catch(() => setVerticals([]));
    }, [id]);

    const addToCart = async (product, newQty, prevQty, variantId = undefined) => {
        if (!user) { nav("/login", { state: { from: `/restaurant/${id}` } }); return; }
        if (newQty > prevQty) {
            // Resolve the picked variant (if any) so cart line carries the
            // variant's own ``selling_price`` + ``mrp`` rather than the
            // product-level fallback. Backend re-validates anyway, but
            // sending the right price avoids a stale-cart toast for
            // products where each variant has very different pricing.
            const variant = variantId
                ? (product.variants || []).find((v) => v.id === variantId)
                : null;
            const item = {
                product_id: product.id,
                name: variant ? `${product.name} — ${variant.label}` : product.name,
                quantity: newQty - prevQty,
                selling_price: variant ? variant.selling_price : product.selling_price,
                image_url: variant?.image_url || product.images?.[0],
                food_type: product.food_type,
                variant_id: variantId,
            };
            const doPost = (force) => api.post(`/cart/items?restaurant_id=${id}${force ? "&force=true" : ""}`, item);
            try {
                await doPost(false);
                window.dispatchEvent(new Event("cart:update"));
                toast.success(`Added ${product.name}`);
            } catch (e) {
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                if (status === 409 && detail && typeof detail === "object" && detail.code === "DIFFERENT_RESTAURANT") {
                    const existing = detail.existing_restaurant || "another restaurant";
                    const ok = window.confirm(`Your cart has items from ${existing}.\nWe can only deliver from one restaurant at a time. Clear cart and start a new order from ${rd?.name || "this restaurant"}?`);
                    if (!ok) { throw e; }
                    try {
                        await doPost(true);
                        window.dispatchEvent(new Event("cart:update"));
                        toast.success(`Cart cleared. Added ${product.name}`);
                        return;
                    } catch (e2) {
                        toast.error(e2?.response?.data?.detail || "Failed");
                        throw e2;
                    }
                }
                toast.error(typeof detail === "string" ? detail : "Failed");
                throw e;
            }
        } else if (newQty === 0) {
            // Multi-shop checkout tenants: the cart may hold other shops' items, so only THIS product is removed.
            // Every other tenant: unchanged (the cart is emptied, as it always was).
            if (multiShopOn(tenant)) await api.patch(`/cart/items/${product.id}?quantity=0${variantId ? `&variant_id=${variantId}` : ""}`);
            else await api.delete("/cart");
            window.dispatchEvent(new Event("cart:update"));
        }
    };

    // Group products by category, preserving the order from rd.categories.
    // Only food / meat / grocery shops show the veg / non-veg pills — uses
    // the shared isFoodShop() helper (same one AdminRestaurants/AdminProducts/
    // MerchantMenu/HomePage/SearchPage use) so a shop stored via UUID
    // business_category_ids resolves correctly instead of the raw id
    // never matching the ["food","meat","grocery"] slug set.
    const showVegFilter = useMemo(
        () => isFoodShop(rd?.restaurant, verticals),
        [rd, verticals],
    );

    const groups = useMemo(() => {
        if (!rd) return [];
        const qn = q.trim().toLowerCase();
        const matches = (p) => {
            if (showVegFilter && foodType && p.food_type !== foodType) return false;
            if (qn && !p.name.toLowerCase().includes(qn)) return false;
            return true;
        };
        const cats = rd.categories || [];
        const result = cats.map((c) => ({
            id: c.id, name: c.name,
            items: products.filter((p) => matches(p) && (p.category_id === c.id || (p.category_ids || []).includes(c.id))),
        }));
        // Uncategorised
        const cat_ids = new Set(cats.map((c) => c.id));
        const orphan = products.filter((p) => matches(p) && !cat_ids.has(p.category_id) && !(p.category_ids || []).some((x) => cat_ids.has(x)));
        if (orphan.length) result.push({ id: UNCATEGORIZED, name: "Others", items: orphan });
        return result.filter((g) => g.items.length > 0);
    }, [rd, products, q, foodType]);

    const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

    const scrollTo = (catId) => {
        setMenuOpen(false);
        // Make sure the section is expanded before scrolling
        setCollapsed((c) => ({ ...c, [catId]: false }));
        // Defer scroll a tick so the DOM updates
        setTimeout(() => {
            const el = sectionRefs.current[catId];
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top, behavior: "smooth" });
        }, 50);
    };

    return {
        id, nav, user, tenant, rd, products, verticals, q, setQ, collapsed, setCollapsed, foodType, setFoodType,
        menuOpen, setMenuOpen, sectionRefs, addToCart, showVegFilter, groups, totalItems, scrollTo,
    };
}
